import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Button,
  Stack,
  MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import ReportMetricCard from './ReportMetricCard';

const csvHeaders = [
  { label: 'Invoice No', key: 'invoice_no' },
  { label: 'Product Name', key: 'product_name' },
  { label: 'Quantity', key: 'qty' },
  { label: 'Cashier', key: 'cashier' },
  { label: 'Payment Method', key: 'payment_method' },
  { label: 'Line Gross Total', key: 'gross_total' },
  { label: 'Bill Net Total', key: 'net_total' },
  { label: 'Bill Discount', key: 'discount' },
  { label: 'Bill Date/Time', key: 'bill_date_time' },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value ?? '-';
  }
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(today),
  };
};

const AllSalesReport = () => {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [sales, setSales] = useState([]);
  const [billHeaders, setBillHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [productQuery, setProductQuery] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchSales = async () => {
      setLoading(true);
      setError('');
      try {
        const [detailResponse, headerResponse] = await Promise.all([
          axios.get('https://serverkf.ccsoftwares.com/sales/getAllBills'),
          axios.get('https://serverkf.ccsoftwares.com/billing/getBillHeaders'),
        ]);

        const headerMap = new Map(
          (headerResponse.data || []).map((header) => [header.inv_no, header])
        );
        const enrichedHeaders = (headerResponse.data || []).map((header) => {
          const billTimestamp = header.creation_date || header.inv_date;
          return {
            ...header,
            billDateObj: billTimestamp ? new Date(billTimestamp) : null,
            grossValue: Number(header.gross_total || 0),
            discountValue: Number(header.discount || 0),
            netValue: Number(header.net_total || 0),
            paymentMethod: header.paymentMethod || 'Unknown',
          };
        });

        const combined = (detailResponse.data || []).map((detail) => {
          const header = headerMap.get(detail.inv_no) || {};
          const billTimestamp = detail.bill_creation_date || detail.creation_date || header.creation_date || header.inv_date;
          const billDateObj = billTimestamp ? new Date(billTimestamp) : null;

          return {
            invoice_no: detail.inv_no,
            product_name: detail.item_name,
            qty: detail.qty,
            cashier: detail.username || header.username || '-',
            payment_method: header.paymentMethod || detail.bill_payment_method || 'Unknown',
            gross_total: detail.total ?? 0,
            net_total: header.net_total ?? detail.bill_net_total ?? 0,
            discount: header.discount ?? 0,
            bill_date_time: billDateObj ? billDateObj.toLocaleString() : 'N/A',
            billDateObj,
          };
        });

        combined.sort((a, b) => {
          if (a.billDateObj && b.billDateObj) {
            return b.billDateObj - a.billDateObj;
          }
          if (a.billDateObj) return -1;
          if (b.billDateObj) return 1;
          return 0;
        });

        setSales(combined);
        setBillHeaders(enrichedHeaders);
      } catch (err) {
        console.error('Failed to load sales data', err);
        setError('Unable to load sales data at the moment.');
        setBillHeaders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSales();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [fromDate, toDate, productQuery, paymentMethod]);

  const paymentMethodOptions = useMemo(
    () => [...new Set(billHeaders.map((header) => header.paymentMethod).filter(Boolean))].sort(),
    [billHeaders]
  );

  const filteredSales = useMemo(() => {
    if (!sales.length) return [];

    const normalizedQuery = productQuery.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    if (from) {
      from.setHours(0, 0, 0, 0);
    }
    const to = toDate ? new Date(toDate) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return sales.filter((row) => {
      const matchesProduct = normalizedQuery
        ? row.product_name?.toLowerCase().includes(normalizedQuery)
        : true;

      if (!matchesProduct) {
        return false;
      }

      if (paymentMethod && row.payment_method !== paymentMethod) {
        return false;
      }

      if (!from && !to) {
        return true;
      }

      if (!row.billDateObj) {
        return false;
      }

      const afterFrom = from ? row.billDateObj >= from : true;
      const beforeTo = to ? row.billDateObj <= to : true;

      return afterFrom && beforeTo;
    });
  }, [sales, fromDate, toDate, productQuery, paymentMethod]);

  const filteredHeadersByDate = useMemo(() => {
    if (!billHeaders.length) return [];

    const from = fromDate ? new Date(fromDate) : null;
    if (from) {
      from.setHours(0, 0, 0, 0);
    }
    const to = toDate ? new Date(toDate) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return billHeaders.filter((header) => {
      if (paymentMethod && header.paymentMethod !== paymentMethod) {
        return false;
      }

      if (!from && !to) {
        return true;
      }

      if (!header.billDateObj) {
        return false;
      }

      const afterFrom = from ? header.billDateObj >= from : true;
      const beforeTo = to ? header.billDateObj <= to : true;
      return afterFrom && beforeTo;
    });
  }, [billHeaders, fromDate, toDate, paymentMethod]);

  const todaysNetSales = useMemo(() => {
    if (!billHeaders.length) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return billHeaders.reduce((accumulator, header) => {
      if (paymentMethod && header.paymentMethod !== paymentMethod) return accumulator;
      if (!header.billDateObj) return accumulator;
      const headerDate = new Date(header.billDateObj);
      headerDate.setHours(0, 0, 0, 0);
      if (headerDate.getTime() === today.getTime()) {
        return accumulator + Number(header.netValue || 0);
      }
      return accumulator;
    }, 0);
  }, [billHeaders, paymentMethod]);

  const selectedPeriodNetSales = useMemo(
    () =>
      filteredHeadersByDate.reduce(
        (accumulator, header) => accumulator + Number(header.netValue || 0),
        0
      ),
    [filteredHeadersByDate]
  );

  const selectedPeriodDiscount = useMemo(
    () =>
      filteredHeadersByDate.reduce(
        (accumulator, header) => accumulator + Number(header.discountValue || 0),
        0
      ),
    [filteredHeadersByDate]
  );

  const csvData = useMemo(
    () =>
      filteredSales.map((row) => ({
        invoice_no: row.invoice_no,
        product_name: row.product_name,
        qty: row.qty,
        cashier: row.cashier,
        payment_method: row.payment_method,
        gross_total: row.gross_total,
        net_total: row.net_total,
        discount: row.discount,
        bill_date_time: row.bill_date_time,
      })),
    [filteredSales]
  );

  const paginatedSales = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredSales;
    }
    const startIndex = page * rowsPerPage;
    return filteredSales.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredSales, page, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            All Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review billed line items with flexible date and product filters.
          </Typography>
        </Box>
        <CSVLink data={csvData} headers={csvHeaders} filename="all-sales-report.csv" style={{ textDecoration: 'none' }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredSales.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <ReportMetricCard
            label="Today's Net Sales"
            value={`Rs. ${formatCurrency(todaysNetSales)}`}
            tooltip="Net sales from unique invoice headers: net_total after discount and service charge. Product search does not change this card."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ReportMetricCard
            label="Selected Period Net Sales"
            value={`Rs. ${formatCurrency(selectedPeriodNetSales)}`}
            tooltip="Net sales from unique invoice headers in the selected date range and payment method: net_total after discount and service charge. Product search does not change this card."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ReportMetricCard
            label="Selected Period Discounts"
            value={`Rs. ${formatCurrency(selectedPeriodDiscount)}`}
            tooltip="Invoice-level discount total from unique invoice headers in the selected date range and payment method. Product search does not change this card."
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Search by Product"
              placeholder="Start typing product name"
              fullWidth
              value={productQuery}
              onChange={(event) => setProductQuery(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Payment Method"
              fullWidth
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value)}
            >
              <MenuItem value="">All Payment Methods</MenuItem>
              {paymentMethodOptions.map((method) => (
                <MenuItem key={method} value={method}>
                  {method}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#9E1713' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Invoice No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Product Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Qty</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Cashier</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Payment Method</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Line Gross Total (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Bill Net Total (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Bill Discount (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Bill Date / Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No sales records match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSales.map((row, index) => (
                  <TableRow key={`${row.invoice_no}-${row.product_name}-${index}`}>
                    <TableCell>{row.invoice_no}</TableCell>
                    <TableCell>{row.product_name}</TableCell>
                    <TableCell>{row.qty}</TableCell>
                    <TableCell>{row.cashier}</TableCell>
                    <TableCell>{row.payment_method}</TableCell>
                    <TableCell>{formatCurrency(row.gross_total)}</TableCell>
                    <TableCell>{formatCurrency(row.net_total)}</TableCell>
                    <TableCell>{formatCurrency(row.discount)}</TableCell>
                    <TableCell>{row.bill_date_time}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && !error && (
        <TablePagination
          component="div"
          count={filteredSales.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
};

export default AllSalesReport;
